import React, { useState, useEffect, ChangeEvent } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store/rootReducer";
import { configThunk } from "../../../../../redux/action/config/config.thunk";
import {
  PurchaseOrder,
  Supplier,
  EOProduct,
  PurchaseOrderRequest,
  PurchaseOrders,
  InputDeliveryRequest,
  EopRequest,
} from "../../../../../models/config.model";
import { configService } from "../../../../../Services/configService";
import { useAlert } from "react-alert";
import { useLoadingMessage } from "../../../../../components/useLoadingMessage";
import Select from "react-select";

interface PurchaseOrderOption {
  value: string;
  label: string;
}

interface EopWithReceived extends EOProduct {
  quantityReceived: number;
  deliveredQuantity: number;
  currentExpectedQuantity: number;
  remainingQuantity: number;
  maxQuantity: number;
}

interface InputDeliveryFormProps {
  open: boolean;
  toggle: () => void;
  refreshData: () => void;
  supplierId?: number;
  stateId?: number;
  purchaseOrderId: string;
  warehouseUniqueId?: string; // Add the warehouse unique ID prop
}

export interface PurchaseOrderResponse {
  message: string;
  errors: string | null;
  data: PurchaseOrders;
}

const WInputDeliveries: React.FC<InputDeliveryFormProps> = ({
  open,
  toggle,
  refreshData,
  supplierId,
  stateId,
  purchaseOrderId,
  warehouseUniqueId, // Get the warehouse unique ID from props
}) => {
  const [selectedSupplier, setSelectedSupplier] = useState<number | null>(
    supplierId || null
  );
  const [deliveryImage, setDeliveryImage] = useState<File | null>(null);
  const [eoPs, setEoPs] = useState<EopWithReceived[]>([]);
  const { isLoading, message, updateLoader } = useLoadingMessage();
  const [purchaseOrderData, setPurchaseOrderData] = useState<PurchaseOrder[]>(
    []
  );
  const [purchaseOrderOptions, setPurchaseOrderOptions] = useState<
    PurchaseOrderOption[]
  >([]);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] =
    useState<PurchaseOrderOption | null>(null);
  const [managerId, setManagerId] = useState<string | null>(null);
  const [selectedWarehouseUniqueId, setSelectedWarehouseUniqueId] =
    useState<string>(warehouseUniqueId || "");
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(
    null
  );
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const { suppliers } = useSelector((state: RootState) => state.suppliers);

  const dispatch = useDispatch();
  const alert = useAlert();

  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setDeliveryImage(file);
    }
  };

  const getPurchaseOrdersByState = async (stateId: number) => {
    updateLoader(true, "Fetching Purchase Orders");
    try {
      const response = await configService.getPurcahseOrderByState(stateId);
      const orders = response?.data?.response || [];
      console.log("PO ByState", orders);

      // Filter orders by the current warehouse
      const filteredOrders = warehouseUniqueId
        ? orders.filter(
            (order: any) => order.warehouseUniqueId === warehouseUniqueId
          )
        : orders;

      setPurchaseOrderData(filteredOrders);

      const options = filteredOrders.map((order: any) => ({
        value: order.id.toString(),
        label: `Order Number ${order.id} Supplier Name: ${
          order.supplierName
        } ${new Date(order.createdAt).toLocaleDateString()}`,
      }));

      setPurchaseOrderOptions(options);
    } catch (error) {
      alert.error("Error fetching purchase orders");
    } finally {
      updateLoader(false, "");
    }
  };

  const getPurchaseOrders = async () => {
    updateLoader(true, "Fetching Purchase Orders");
    try {
      const response = await configService.getAllPurchaseOrder();

      const orders = response?.data?.data || [];
      console.log("All POs", orders);

      // Filter orders by the current warehouse
      const filteredOrders = warehouseUniqueId
        ? orders.filter(
            (order: any) => order.warehouseUniqueId === warehouseUniqueId
          )
        : orders;

      setPurchaseOrderData(filteredOrders);

      const options = filteredOrders.map((order: any) => ({
        value: order.id.toString(),
        label: `Order Number ${order.id} | Supplier: ${
          order.supplierName
        } | ${new Date(order.createdAt).toLocaleDateString()}`,
      }));

      console.log("Setting dropdown options:", options);
      setPurchaseOrderOptions(options);
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
      alert.error("Error fetching purchase orders");
    } finally {
      updateLoader(false, "");
    }
  };

  const loadPurchaseOrder = async (orderId: number) => {
    updateLoader(true, "Loading Purchase Order Details");
    try {
      const response: { data: PurchaseOrderResponse } =
        await configService.getOnePurchaseOrder(orderId);
      const poData = response.data.data;

      // Check if the purchase order belongs to the current warehouse
      if (warehouseUniqueId && poData.warehouseUniqueId !== warehouseUniqueId) {
        setErrorMessages([
          "This purchase order is not associated with the current warehouse",
        ]);
        return;
      }

      setSelectedWarehouseUniqueId(poData.warehouseUniqueId);
      setSelectedSupplierId(poData.supplierId);
      setManagerId(poData.managerId);

      const formattedEoPs = poData.purchaseOrderEOPs.map((eop: any) => ({
        id: eop.eopId,
        name: eop.eopName,
        quantityRequested: eop.quantityRequested,
        deliveredQuantity: eop.deliveredQuantity,
        currentExpectedQuantity: eop.quantityRequested - eop.deliveredQuantity,
        remainingQuantity: eop.quantityRequested - eop.deliveredQuantity,
        quantityReceived: 0,
        maxQuantity: eop.quantityRequested,
        quantity: eop.quantityRequested - eop.deliveredQuantity,
      }));

      setEoPs(formattedEoPs);
    } catch (error) {
      alert.error("Error loading purchase order details");
    } finally {
      updateLoader(false, "");
    }
  };

  const handlePurchaseOrderChange = (option: PurchaseOrderOption | null) => {
    setSelectedPurchaseOrder(option);
    if (option) {
      loadPurchaseOrder(parseInt(option.value));
    }
  };

  const handleQuantityReceivedChange = (
    index: number,
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const updatedQuantityReceived = parseInt(e.target.value) || 0;

    setEoPs((prevEoPs) => {
      return prevEoPs.map((eoProduct, i) => {
        if (i === index) {
          const originalQuantity = eoProduct.quantity;
          // const validQuantityReceived = Math.min(
          //   updatedQuantityReceived,
          //   originalQuantity
          // );
          // const newRemainingQuantity = Math.max(
          //   0,
          //   originalQuantity - validQuantityReceived
          // );

          if (updatedQuantityReceived > originalQuantity) {
            setErrorMessages([
              `Quantity cannot exceed available quantity of ${originalQuantity}`,
            ]);
            return eoProduct; // Return unchanged if invalid
          }

          setErrorMessages([]); // Clear errors if valid

          return {
            ...eoProduct,
            quantityReceived: updatedQuantityReceived,
            remainingQuantity: originalQuantity - updatedQuantityReceived,
          };
        }
        return eoProduct;
      });
    });
  };

  const getroles = React.useCallback(async () => {
    await dispatch(configThunk.getAllSuppliers(suppliers));
  }, [suppliers]);

  useEffect(() => {
    // Initialize the selected warehouse unique ID if it's provided
    if (warehouseUniqueId) {
      setSelectedWarehouseUniqueId(warehouseUniqueId);
    }

    if (stateId) {
      getPurchaseOrdersByState(stateId);
    } else {
      getPurchaseOrders();
    }

    getroles();
    if (purchaseOrderId) {
      loadPurchaseOrder(parseInt(purchaseOrderId));
    }
  }, [stateId, purchaseOrderId, warehouseUniqueId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedPurchaseOrder) {
      setErrorMessages(["Please select a purchase order"]);
      return;
    }

    const hasItemsToDeliver = eoPs.some((eop) => eop.quantityReceived > 0);

    if (!hasItemsToDeliver) {
      setErrorMessages(["No items to deliver for this supplier."]);
      return;
    }

    const inputDeliveryRequest: InputDeliveryRequest = {
      warehouseUniqueId: selectedWarehouseUniqueId,
      managerId: managerId ?? undefined,
      stateId: stateId,
      purchaseOrderId: parseInt(selectedPurchaseOrder.value),
      isAccepted: true,
      eopRequests: eoPs
        .filter((eop) => eop.quantityReceived > 0)
        .map(({ id, quantityReceived }) => ({
          eopId: Number(id),
          quantity: Number(quantityReceived),
        })),
    };

    if (deliveryImage) {
      inputDeliveryRequest.attachedFile = deliveryImage.name;
    }

    try {
      const response = await configService.inputDelivery(inputDeliveryRequest);
      alert.success(response.data.message);
      refreshData();
      setSelectedPurchaseOrder(null);
      setEoPs([]);
      setDeliveryImage(null);
      setSelectedSupplierId(null);

      toggle();
    } catch (error: any) {
      if (error.response?.data?.errors) {
        const errors = Object.entries(error.response.data.errors)
          .map(([field, messages]) => {
            if (Array.isArray(messages)) {
              return messages.map((msg) => `${field}: ${msg}`);
            }
            return `${field}: ${messages}`;
          })
          .flat();
        setErrorMessages(errors);
      } else {
        setErrorMessages([
          error.response?.data?.message || "An error occurred",
        ]);
      }
    }
  };

  return (
    <Modal isOpen={open} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Input Delivery</ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={12}>
              <FormGroup>
                <Label for="purchaseOrder">Select Purchase Order</Label>
                <Select
                  id="purchaseOrder"
                  value={selectedPurchaseOrder}
                  onChange={handlePurchaseOrderChange}
                  options={purchaseOrderOptions}
                  isSearchable
                  placeholder="Select a purchase order..."
                />
              </FormGroup>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={12}>
              <FormGroup>
                <Label for="supplier">Supplier</Label>
                <Select
                  name="supplier"
                  id="supplier"
                  value={suppliers
                    .map((supplier: Supplier) => ({
                      label: supplier.name,
                      value: supplier.id,
                    }))
                    .find((option) => option.value === selectedSupplierId)}
                  onChange={(option) =>
                    setSelectedSupplier(option?.value || null)
                  }
                  options={suppliers.map((supplier: Supplier) => ({
                    label: supplier.name,
                    value: supplier.id,
                  }))}
                  isDisabled={true}
                />
              </FormGroup>
            </Col>
          </Row>
          {eoPs.map((eoProduct, index) => (
            <Row key={index} className="mb-3 align-items-end">
              <Col md={3}>
                <FormGroup>
                  <Label>Product Name</Label>
                  <Input type="text" value={eoProduct.name} disabled />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Expected Quantity</Label>
                  <Input type="text" value={eoProduct.quantity} disabled />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Received Quantity</Label>
                  <Input
                    type="number"
                    value={eoProduct.quantityReceived}
                    onChange={(e) => handleQuantityReceivedChange(index, e)}
                    min={0}
                    max={eoProduct.quantity}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Remaining Quantity</Label>
                  <Input
                    type="text"
                    value={eoProduct.remainingQuantity}
                    disabled
                  />
                </FormGroup>
              </Col>
            </Row>
          ))}

          <FormGroup className="mt-4">
            <Label for="deliveryImage">Upload Delivery Image</Label>
            <Input
              type="file"
              id="deliveryImage"
              name="deliveryImage"
              accept="image/*"
              onChange={handleImageUpload}
              style={{
                width: "100%",
                padding: "0.375rem 0.75rem",
                border: "1px solid #ced4da",
                borderRadius: "0.25rem",
                lineHeight: "1.5",
                backgroundColor: "#fff",
              }}
            />
          </FormGroup>
          <Button type="submit" color="success" className="w-100 mt-3 mb-2">
            Save Delivery
          </Button>
          {errorMessages.length > 0 && (
            <div className="alert alert-danger mb-3">
              <button
                type="button"
                className="close"
                onClick={() => setErrorMessages([])}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
              <ul className="mb-0 pl-3">
                {errorMessages.map((msg, index) => (
                  <li key={index}>{msg}</li>
                ))}
              </ul>
            </div>
          )}
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default WInputDeliveries;
